import { useState } from 'react';

import { areSameRows, createEmptyRow, reconcileRows } from './audienceFilterRows';

import type { AudienceFilterRow } from './audienceFilterRows';
import type { CompanyAttributeMap } from '@/utils/company';

interface AssignAttributeParams {
    rowId: string;
    attributeName: string;
}

export interface AudienceFilterRowsController {
    rows: AudienceFilterRow[];
    addRow: () => void;
    dropRow: (id: string) => void;
    assignAttribute: (params: AssignAttributeParams) => void;
}

const toAttributeNames = (selectedCompanyAttributes: CompanyAttributeMap): string[] =>
    Object.values(selectedCompanyAttributes ?? {}).map((attribute) => attribute.name);

export const useAudienceFilterRows = (
    selectedCompanyAttributes: CompanyAttributeMap,
): AudienceFilterRowsController => {
    const [rows, setRows] = useState<AudienceFilterRow[]>([]);

    const reconciled = reconcileRows({
        rows,
        selectedAttributeNames: toAttributeNames(selectedCompanyAttributes),
    });

    // Reconciling here rather than in an effect keeps the rows keyed on the selection's CONTENT:
    // an effect would have to key on the caller's object reference, and a caller that rebuilt that
    // object each render would make a removed row reappear.
    if (!areSameRows({ left: rows, right: reconciled })) {
        setRows(reconciled);
    }

    const addRow = () => setRows((current) => [...current, createEmptyRow(current)]);

    const dropRow = (id: string) =>
        setRows((current) => {
            const remaining = current.filter((row) => row.id !== id);

            return remaining.length > 0 ? remaining : [createEmptyRow([])];
        });

    const assignAttribute = ({ rowId, attributeName }: AssignAttributeParams) =>
        setRows((current) =>
            current.map((row) => (row.id === rowId ? { ...row, attributeName } : row)),
        );

    return { rows: reconciled, addRow, dropRow, assignAttribute };
};
